/**
 * React hook: request media from Media Master (on demand + cached).
 */
import { useEffect, useState, useCallback } from 'react';
import { mediaMaster } from './mediaMaster';
import type { ResolvedMedia } from './types';

export interface UseMediaState {
  media: ResolvedMedia | null;
  loading: boolean;
  error: Error | null;
  reload: () => void;
}

export function useMedia(scope: string, variable: string): UseMediaState {
  const [media, setMedia] = useState<ResolvedMedia | null>(() =>
    mediaMaster.getMediaIfCached(scope, variable)
  );
  const [loading, setLoading] = useState(!mediaMaster.getMediaIfCached(scope, variable));
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(() => {
    mediaMaster.invalidateResolution(scope, variable);
    setMedia(null);
    setError(null);
    setLoading(true);
    mediaMaster
      .getMedia(scope, variable)
      .then(setMedia)
      .catch((e: unknown) => {
        setError(e instanceof Error ? e : new Error(String(e)));
        setMedia(null);
      })
      .finally(() => setLoading(false));
  }, [scope, variable]);

  useEffect(() => {
    let cancelled = false;
    const cached = mediaMaster.getMediaIfCached(scope, variable);
    if (cached) {
      setMedia(cached);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    mediaMaster
      .getMedia(scope, variable)
      .then((m) => {
        if (!cancelled) setMedia(m);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
          setMedia(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scope, variable]);

  return { media, loading, error, reload };
}
